import React, { useEffect, useRef, useState } from 'react';
import {
  createStakeholder, updateStakeholder, stakeholderInitials, STAKEHOLDER_TYPES,
} from '../data/stakeholders';
import ConfirmDialog from '../components/ConfirmDialog';
import './Stakeholders.css';

const BLANK_CONTACT = { name: '', role: '', phone: '', email: '', isPrimary: false };

// Downscale a picked image to a small square-ish JPEG data URL, so a stakeholder
// photo persists in the (TEXT) logo column without shipping a multi-MB blob.
function resizeImage(file, max = 240) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

function emptyForm(seed = {}) {
  return {
    name: seed.name || '', type: seed.type || STAKEHOLDER_TYPES[0], role: seed.role || '',
    email: seed.email || '', phone: seed.phone || '', address: '', website: '', notes: '',
    logo: '', officeLogo: '',
    contacts: [{ ...BLANK_CONTACT, name: seed.contact || '', email: seed.email || '', phone: seed.phone || '', isPrimary: true }],
  };
}

function toForm(s) {
  return {
    name: s.name || '', type: s.type || STAKEHOLDER_TYPES[0], role: s.role || '',
    email: s.email || '', phone: s.phone || '', address: s.address || '',
    website: s.website || '', notes: s.notes || '', logo: s.logo || '',
    officeLogo: s.office_logo || s.officeLogo || '',
    contacts: (s.contacts && s.contacts.length
      ? s.contacts.map((c) => ({
          name: c.name || '', role: c.role || '', phone: c.phone || '',
          email: c.email || '', isPrimary: Boolean(c.is_primary ?? c.isPrimary),
        }))
      : [{ ...BLANK_CONTACT, isPrimary: true }]),
  };
}

// Create/edit dialog for a directory stakeholder. Used from the בעלי עניין screen
// and (spec 1.2/1.3) from the project-details page. `onSaved(result)` receives
// the created/updated record so callers can reflect it locally.
export default function StakeholderModal({ stakeholder, seed, onClose, onSaved }) {
  const isEdit = Boolean(stakeholder && stakeholder.id);
  const [form, setForm] = useState(() => (isEdit ? toForm(stakeholder) : emptyForm(seed)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // While true, the first contact mirrors the org email/phone as they're typed,
  // so a new stakeholder is never saved without a basic contact (spec 1.4).
  const [contactAuto, setContactAuto] = useState(!isEdit);

  // Exit guard (spec 3.2): confirm before discarding unsaved edits.
  const [confirmClose, setConfirmClose] = useState(false);
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(form);
  const isDirty = () => JSON.stringify(form) !== initialSig.current;
  const requestClose = () => { if (isDirty()) setConfirmClose(true); else onClose(); };

  const modalRef = useRef(null);
  const photoInputRef = useRef(null);
  const officeInputRef = useRef(null);
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  const onImagePick = (field) => async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('יש לבחור קובץ תמונה.'); return; }
    try {
      const dataUrl = await resizeImage(file, field === 'officeLogo' ? 320 : 240);
      setForm((f) => ({ ...f, [field]: dataUrl }));
      setError('');
    } catch {
      setError('טעינת התמונה נכשלה.');
    }
  };

  // Accessibility: focus the dialog on open, keep Tab inside it, close on Esc,
  // and restore focus to the trigger on unmount.
  useEffect(() => {
    const node = modalRef.current;
    const prevFocus = document.activeElement;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () => Array.from(node.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
    const firstField = node.querySelector('.sh-modal-body input, .sh-modal-body select, .sh-modal-body textarea');
    (firstField || focusable()[0] || node).focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); requestCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const idx = items.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0) { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && idx === items.length - 1) { e.preventDefault(); items[0].focus(); }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus();
    };
  }, []);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (contactAuto && (k === 'email' || k === 'phone')) {
      next.contacts = f.contacts.map((c, idx) => (idx === 0 ? { ...c, [k]: v } : c));
    }
    return next;
  });
  const setContact = (i, k, v) => {
    setContactAuto(false);
    setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  };
  const addContact = () => { setContactAuto(false); setForm((f) => ({ ...f, contacts: [...f.contacts, { ...BLANK_CONTACT }] })); };
  const removeContact = (i) =>
    setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));

  async function save() {
    if (!form.name.trim()) { setError('יש להזין שם חברה / ארגון.'); return; }
    if (!form.role.trim()) { setError('יש להזין תפקיד.'); return; }
    if (!form.email.trim()) { setError('יש להזין אימייל.'); return; }
    setError('');
    setSaving(true);
    const payload = {
      name: form.name, type: form.type, role: form.role, email: form.email,
      phone: form.phone, address: form.address, website: form.website, notes: form.notes,
      logo: form.logo || '', officeLogo: form.officeLogo || '',
      contacts: form.contacts.filter((c) => c.name || c.email || c.phone),
    };
    try {
      let id = stakeholder && stakeholder.id;
      if (isEdit) await updateStakeholder(id, payload);
      else { const r = await createStakeholder(payload); id = r?.id || id; }
      onSaved({ id, ...payload });
    } catch (e) {
      setError(e.message || 'השמירה נכשלה.');
      setSaving(false);
    }
  }

  return (
    <div className="sh-modal-backdrop" onMouseDown={requestClose}>
      <div ref={modalRef} className="sh-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={isEdit ? 'עריכת בעל עניין' : 'בעל עניין חדש'} tabIndex={-1}>
        <header className="sh-modal-head">
          <h2>{isEdit ? 'עריכת בעל עניין' : 'בעל עניין חדש'}</h2>
          <button type="button" className="sh-modal-x" onClick={requestClose} aria-label="סגור">
            <i className="fi fi-rr-cross-small" />
          </button>
        </header>

        <div className="sh-modal-body">
          {error && <div className="sh-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

          <div className="sh-images">
            {/* Personal / profile photo — shown on the card (spec 1.5). */}
            <div className="sh-photo-row">
              <div className="sh-photo">
                {form.logo
                  ? <img src={form.logo} alt="" />
                  : <span className="sh-photo-initials">{stakeholderInitials(form.name) || '—'}</span>}
              </div>
              <div className="sh-photo-actions">
                <span className="sh-photo-title">תמונה אישית</span>
                <div className="sh-photo-links">
                  <button type="button" className="sh-link" onClick={() => photoInputRef.current?.click()}>
                    {form.logo ? 'החלפה' : 'העלאה'}
                  </button>
                  {form.logo && (
                    <button type="button" className="sh-link sh-link-muted" onClick={() => set('logo', '')}>הסרה</button>
                  )}
                </div>
                <input ref={photoInputRef} type="file" accept="image/*" className="sh-photo-input" onChange={onImagePick('logo')} />
              </div>
            </div>

            {/* Office / firm logo — reused in the final report (spec 1.6). */}
            <div className="sh-photo-row">
              <div className="sh-photo sh-photo-logo">
                {form.officeLogo
                  ? <img src={form.officeLogo} alt="" />
                  : <i className="fi fi-rr-building" />}
              </div>
              <div className="sh-photo-actions">
                <span className="sh-photo-title">לוגו המשרד</span>
                <div className="sh-photo-links">
                  <button type="button" className="sh-link" onClick={() => officeInputRef.current?.click()}>
                    {form.officeLogo ? 'החלפה' : 'העלאה'}
                  </button>
                  {form.officeLogo && (
                    <button type="button" className="sh-link sh-link-muted" onClick={() => set('officeLogo', '')}>הסרה</button>
                  )}
                </div>
                <input ref={officeInputRef} type="file" accept="image/*" className="sh-photo-input" onChange={onImagePick('officeLogo')} />
              </div>
            </div>
          </div>

          <div className="sh-grid">
            <label className="sh-field">
              <span>שם החברה / הארגון<i className="sh-req">*</i></span>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="לדוגמה: בר-לב אדריכלים" />
            </label>
            <label className="sh-field">
              <span>סיווג / סוג<i className="sh-req">*</i></span>
              <select value={form.type} onChange={(e) => set('type', e.target.value)}>
                {STAKEHOLDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="sh-field">
              <span>תפקיד<i className="sh-req">*</i></span>
              <input value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="לדוגמה: אדריכל" />
            </label>
            <label className="sh-field">
              <span>אימייל<i className="sh-req">*</i></span>
              <input className="sh-ltr" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@company.co.il" />
            </label>
            <label className="sh-field">
              <span>טלפון</span>
              <input className="sh-ltr" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="050-0000000" />
            </label>
            <label className="sh-field">
              <span>אתר</span>
              <input className="sh-ltr" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="www.company.co.il" />
            </label>
            <label className="sh-field sh-col-2">
              <span>כתובת</span>
              <input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="רחוב, עיר" />
            </label>
            <label className="sh-field sh-col-2">
              <span>הערות</span>
              <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="הערות נוספות" />
            </label>
          </div>

          <div className="sh-contacts">
            <div className="sh-contacts-head">
              <h3>אנשי קשר</h3>
              <button type="button" className="sh-link" onClick={addContact}>+ הוסף איש קשר</button>
            </div>
            {form.contacts.map((c, i) => (
              <div className="sh-contact" key={i}>
                <input placeholder="שם מלא" value={c.name} onChange={(e) => setContact(i, 'name', e.target.value)} />
                <input placeholder="תפקיד" value={c.role} onChange={(e) => setContact(i, 'role', e.target.value)} />
                <input className="sh-ltr" placeholder="טלפון" value={c.phone} onChange={(e) => setContact(i, 'phone', e.target.value)} />
                <input className="sh-ltr" placeholder="אימייל" value={c.email} onChange={(e) => setContact(i, 'email', e.target.value)} />
                <label className="sh-primary" title="איש קשר ראשי">
                  <input type="radio" name="primary-contact" checked={c.isPrimary}
                    onChange={() => setForm((f) => ({ ...f, contacts: f.contacts.map((x, idx) => ({ ...x, isPrimary: idx === i })) }))} />
                  <span>ראשי</span>
                </label>
                <button type="button" className="sh-contact-x" aria-label="הסר" onClick={() => removeContact(i)}>
                  <i className="fi fi-rr-trash" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <footer className="sh-modal-foot">
          <button type="button" className="sh-btn sh-btn-ghost" onClick={requestClose} disabled={saving}>ביטול</button>
          <button type="button" className="sh-btn sh-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </footer>
      </div>

      {confirmClose && (
        <ConfirmDialog
          title="לצאת בלי לשמור?"
          message="יש שינויים שלא נשמרו בפרטי בעל העניין."
          onDismiss={() => setConfirmClose(false)}
          actions={[
            { label: 'המשך עריכה', tone: 'ghost', onClick: () => setConfirmClose(false) },
            { label: 'צא בלי לשמור', tone: 'danger', onClick: () => { setConfirmClose(false); onClose(); } },
            { label: saving ? 'שומר…' : 'שמור', tone: 'primary', onClick: () => { setConfirmClose(false); save(); }, disabled: saving },
          ]}
        />
      )}
    </div>
  );
}
